import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  SurveyBuilderFields,
  SurveyBuilderConfig,
  SurveyBuilderVersionDocument,
  SurveyBuilderRootDocument,
  SurveyBuilderFieldsContentOnly,
  SurveyStatus,
  SurveyBuilderFlags,
  SurveyTranslatableContent,
} from "@max/common/dist/setfan";
import {
  collection,
  DocumentSnapshot,
  getFirestore,
  query,
  setDoc,
  updateDoc,
  where,
  Timestamp,
} from "firebase/firestore";
import { buildSurveyBuilderConfig } from "./buildBaseConfig";
import { getFunctions } from "firebase/functions";
import { addToast } from "melodies-source/Toast";
import { getPublishFunction } from "@max/common/dist/functions/setfan/publish";
import { validateConfig } from "./builderConfigSchema";
import { useCollection } from "react-firebase-hooks/firestore";
import { useArtistContext } from "Components";
import { useHistory, useParams } from "react-router-dom";
import { useScrollLockContext } from "contexts/ScrollLockContext";
import { addHttpsProtocolToUrl } from "Utils/url";
import { cloneDeep, isEqual, mapValues } from "lodash";
import { PublishModal } from "./PublishModal";

const publish = getPublishFunction(getFunctions());

export type Fields = keyof SurveyBuilderFields;
export const typedFields = <T extends Fields>(fields: T[]) => fields;
type PublishingStatus = "idle" | "draft" | "live";

interface BuilderI {
  data: SurveyBuilderConfig;
  setData: Dispatch<SetStateAction<SurveyBuilderConfig>>;
  onPublish: (args: { status: "draft" | "live" }) => Promise<boolean>;
  publishingStatus: PublishingStatus;
  status: Extract<SurveyStatus, "live" | "draft" | "completed" | "deleted">;
  hasUnpublishedChanges: boolean;
  hasUnpublishedField: (field: Fields) => boolean;
}

const BuilderContext = createContext<BuilderI>({} as BuilderI);

export const useBuilderContext = () => useContext(BuilderContext);

interface BuilderProviderProps {
  survey: DocumentSnapshot<SurveyBuilderRootDocument>;
  version: DocumentSnapshot<SurveyBuilderVersionDocument>;
  publishedVersion?: DocumentSnapshot<SurveyBuilderVersionDocument>;
}

export const BuilderProvider = ({
  survey,
  version,
  publishedVersion,
  children,
}: PropsWithChildren<BuilderProviderProps>) => {
  const [data, setData] = useState(
    buildSurveyBuilderConfig(survey.data(), version.data()),
  );
  const campaignStatus = survey.data().status;

  const publishedBaseline = useMemo(
    () =>
      getComparable(
        buildSurveyBuilderConfig(
          survey.data(),
          (publishedVersion ?? version).data(),
        ),
      ),
    [survey, publishedVersion, version],
  );
  const [baseline, setBaseline] = useState(publishedBaseline);

  useEffect(() => {
    setBaseline(publishedBaseline);
  }, [publishedBaseline]);

  const hasUnpublishedChanges = useMemo(
    () => !isEqual(getComparable(data), baseline),
    [data, baseline],
  );

  const hasUnpublishedField = useCallback(
    (field: Fields) =>
      !isEqual(data.fields[field]?.content, baseline.fields[field]),
    [data, baseline],
  );

  const history = useHistory();
  const { lockScroll, unlockScroll } = useScrollLockContext();

  const [subdomain, setSubdomain] = useState<string | null>(null);
  const { id } = useArtistContext();
  const [subdomains] = useCollection(
    query(
      collection(getFirestore(), `artist_subdomain`),
      where("artistGroupId", "==", id),
    ),
  );
  const [isPublishing, setIsPublishing] = useState(false);
  const { surveyId } = useParams<{ surveyId: string }>();

  useEffect(() => {
    if (campaignStatus !== "draft") {
      return;
    }

    const timeout = setTimeout(() => {
      const dirtyFields = getDirtyFields(data.fields);
      const dirtyKeys = Object.keys(dirtyFields) as Fields[];

      setDoc(
        version.ref,
        {
          surveyQuestions: data.surveyQuestions,
          flags: data.flags,
          ...(dirtyKeys.length ? { fields: dirtyFields } : {}),
        },
        { merge: true },
      );

      if (dirtyKeys.length) {
        setData((prevData) => {
          const nextData = cloneDeep(prevData);
          dirtyKeys.forEach((k) => (nextData.fields[k].isDirty = false));
          return nextData;
        });
      }
    }, 3000);

    return () => clearTimeout(timeout);
  }, [data, version, campaignStatus]);

  useEffect(() => {
    setSubdomain(subdomains?.docs[0]?.id || null);
  }, [subdomains?.docs.length]);

  const saveToBuilderDoc = async () => {
    const fields = Object.keys(data.fields).reduce((prev, c: Fields) => {
      const field = data.fields[c];

      // append the https: protocol to url if it does not have it (we do not require it when validing form field)
      if (c === "featuredCtaUrl") {
        const urlWithHttps = addHttpsProtocolToUrl(
          (field.content as SurveyTranslatableContent).en,
        );
        return {
          ...prev,
          [c]: { content: { en: urlWithHttps } },
        };
      }

      return { ...prev, [c]: { content: field.content } };
    }, {} as SurveyBuilderFieldsContentOnly);

    await setDoc(version.ref, { ...data, fields }, { merge: true });
  };

  const [publishingStatus, setPublishingStatus] =
    useState<PublishingStatus>("idle");

  const touchFieldsWithErrors = useCallback(() => {
    setData((prevData) => {
      const nextData = cloneDeep(prevData);
      const allFields = Object.keys(nextData.fields) as Fields[];

      allFields.forEach((fieldName) => {
        const field = nextData.fields[fieldName];
        const hasError = !!field.getValidation(nextData);
        if (hasError) {
          field.isTouched = true;
        }
      });

      return nextData;
    });
  }, [setData]);

  const onPublish = async ({
    status,
  }: {
    status: "draft" | "live";
  }): Promise<boolean> => {
    const wasLive = campaignStatus === "live";
    const isFirstPublish = status === "live" && !wasLive;

    try {
      touchFieldsWithErrors();

      if (status === "live") {
        const validToPublish = isPublishValid(data);
        if (!validToPublish) return;
      }

      if (isFirstPublish) {
        lockScroll();
        setIsPublishing(true);
        return;
      }

      const success = await callCloudPublish({ status });
      if (success) {
        onPublishSuccess({ status, stayOnPage: wasLive });
      }
    } catch (err) {
      console.error(`there was an issue publishing: ${(err as Error).message}`);
      addToast(
        `Unable to ${
          status === "draft" ? "save campaign as draft" : "publish campaign"
        }. Your changes in this editor are saved, but the draft or published campaign is not up to date.`,
        "error",
        { duration: 7000 },
      );
    }
  };

  const onPublishSubdomain = async () => {
    const success = await callCloudPublish({ status: "live" });
    if (success) {
      await updateDoc(subdomains.docs[0].ref, { surveyId: survey.id });
      setSubdomain(null);
      onPublishSuccess({ status: "live" });
    }
  };

  const onPublishSuccess = ({
    status,
    stayOnPage = false,
  }: {
    status: SurveyStatus;
    stayOnPage?: boolean;
  }) => {
    setBaseline(getComparable(data));

    if (!stayOnPage) {
      history.push(`/${id}/set-fan/surveys`);
    }
    addToast(
      status === "draft"
        ? "Campaign draft successfully saved"
        : "Campaign successfully published",
      "success",
    );
  };

  const callCloudPublish = async ({ status }: { status: "draft" | "live" }) => {
    setPublishingStatus(status);
    try {
      await saveToBuilderDoc();
      const {
        data: { success },
      } = await publish({ surveyId: survey.id, status });

      if (!success) {
        throw new Error("the call to get publish failed");
      }

      return true;
    } catch (err) {
      console.error(`there was an issue publishing: ${(err as Error).message}`);
      addToast(
        `Unable to ${
          status === "draft" ? "save campaign as draft" : "publish campaign"
        }. Your changes in this editor are saved, but the draft or published campaign is not up to date.`,
        "error",
        { duration: 7000 },
      );
      return false;
    } finally {
      setPublishingStatus("idle");
    }
  };

  return (
    <BuilderContext.Provider
      value={{
        data,
        setData,
        onPublish,
        publishingStatus,
        status: campaignStatus,
        hasUnpublishedChanges,
        hasUnpublishedField,
      }}
    >
      {children}
      <PublishModal
        isOpen={isPublishing}
        onClose={() => {
          unlockScroll();
          setIsPublishing(false);
        }}
        surveyId={surveyId}
        handlePublish={async () => {
          const success = await callCloudPublish({ status: "live" });
          if (success) {
            onPublishSuccess({ status: "live" });
          }
        }}
        handlePublishSubdomain={onPublishSubdomain}
        subdomain={subdomain}
        featuredSurveyId={subdomains?.docs[0]?.data()?.surveyId}
      />
    </BuilderContext.Provider>
  );
};

const isPublishValid = (config: SurveyBuilderConfig): boolean => {
  const errors = validateConfig(config);

  if (errors) {
    addToast(
      <span style={{ display: "block", maxWidth: 400 }}>
        Campaign is incomplete: {errors.join(", ")}
      </span>,
      "error",
    );
    return false;
  }

  const actionRequired = config.surveyQuestions.some((sq) => sq.actionRequired);
  if (actionRequired) {
    addToast("One or more of Campaign Questions requires completion", "error");
    return false;
  }

  return true;
};

const getComparable = ({ fields, ...rest }: SurveyBuilderConfig) => ({
  ...rest,
  fields: mapValues(fields, (field) => field.content),
});

const getDirtyFields = (fields: SurveyBuilderFields) => {
  return Object.keys(fields).reduce((prev, key: Fields) => {
    const field = fields[key];

    return field.isDirty
      ? { ...prev, [key]: { content: field.content } }
      : prev;
  }, {} as SurveyBuilderFieldsContentOnly);
};

export const getBuilderFields = <T extends Fields>(
  config: SurveyBuilderConfig,
  fields: T[],
) => {
  return fields.reduce(
    (p, c) => ({ ...p, [c]: config.fields[c] }),
    {} as Pick<SurveyBuilderConfig["fields"], T>,
  );
};

export const validate = <T extends Fields>(
  config: SurveyBuilderConfig,
  fields: T[],
) => {
  const validation = fields.reduce(
    (p, c) => ({
      ...p,
      [c]: config.fields[c].getValidation(config),
    }),
    {} as Record<T, string | false>,
  );
  const isValid = !Object.values(validation).find((f) => !!f);
  return { validation, isValid };
};

export const useConfigSlice = <T extends Fields>(fields: T[]) => {
  const { data, setData } = useBuilderContext();
  const filtered = getBuilderFields(data, fields);
  const { validation, isValid } = validate(data, fields);

  const setField = useCallback(
    <F extends T>(
      fieldName: F,
      content: SurveyBuilderConfig["fields"][F]["content"],
    ) => {
      setData((prevData) => {
        const nextData = cloneDeep(prevData);
        const field = nextData.fields[fieldName];

        if (
          typeof field.content === "object" &&
          !Array.isArray(field.content) &&
          typeof content === "object" &&
          !Array.isArray(content) &&
          !(field.content instanceof Timestamp) &&
          !(content instanceof Timestamp)
        ) {
          field.content = { ...field.content, ...content };
        } else {
          field.content = content;
        }

        field.isDirty = true;
        return nextData;
      });
    },
    [setData],
  );

  const setFieldTouched = useCallback(
    <F extends T>(fieldName: F) => {
      setData((prevData) => {
        const nextData = cloneDeep(prevData);
        nextData.fields[fieldName].isTouched = true;
        return nextData;
      });
    },
    [setData],
  );

  const setFieldsTouched = () => {
    let ndata = { ...data };
    fields.forEach((fieldName) => (ndata.fields[fieldName].isTouched = true));
    setData(ndata);
  };

  return {
    fields: filtered,
    validation,
    setField,
    setFieldTouched,
    setFieldsTouched,
    isValid,
  };
};

export const useConfigFlags = () => {
  const { data, setData } = useBuilderContext();

  const setFlagValue = useCallback(
    <T extends keyof SurveyBuilderFlags>(flag: T, value: boolean) => {
      setData((prev) => {
        return {
          ...prev,
          flags: {
            ...prev.flags,
            [flag]: value,
          },
        };
      });
    },
    [],
  );

  return {
    flags: data.flags,
    setFlagValue,
  };
};

/**
 * Hook to check if a section has validation errors
 * @param fields - Array of field names to check for validation errors
 * @returns hasError - boolean indicating if any of the fields have validation errors
 */
export const useSectionValidation = <T extends Fields>(fields: T[]) => {
  const { data } = useBuilderContext();
  const { validation } = validate(data, fields);
  const hasError = Object.values(validation).some((v) => !!v);
  return { hasError, validation };
};
